// src/context/AuthContext.tsx
// Skips profile fetch on `/login` and only logs real errors (based on existing AuthContext.tsx) :contentReference[oaicite:2]{index=2}&#8203;:contentReference[oaicite:3]{index=3}

import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { fetchProfile, logout as apiLogout } from '../api/auth';
import { useLocation } from 'react-router-dom';

interface AuthContextType {
  user: string | null;
  setUser: React.Dispatch<React.SetStateAction<string | null>>;
  loading: boolean;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<
  AuthContextType | undefined
>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Don’t call /profile (and trigger 401) on the login page
    if (location.pathname === '/login') {
      setUser(null);
      setLoading(false);
      return;
    }

    fetchProfile()
      .then(profile => {
        // profile === null if 401; otherwise has .username
        setUser(profile?.username ?? null);
      })
      .catch(err => {
        // Only log if it's a genuine error (not a swallowed 401)
        console.error('Error fetching profile:', err);
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [location]);

  const logout = async () => {
    try {
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
